import { Button, InputNumber, InputCurrency } from "@sellar-io/components";
import { useState } from "react";
import {
  InputLabel,
  InputContainer,
  InputOption,
  InputSelect,
} from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { Alert } from "../../../components/alert/Alert";
import { PriceListDerivationType } from "@sellar-io/api-v2/types";
import { formatPriceInputToApi } from "../../../hooks/usePriceFormat";
import { usePriceListUpdateDerived } from "../../../hooks/api/v2/priceList/usePriceListUpdateDerived";

export const ListRuleUpdateDialog = ({
  active,
  setActive,
  derivationType,
  derivationValue,
  id,
}: {
  active: boolean;
  setActive: (active: boolean) => void;
  derivationType: PriceListDerivationType;
  derivationValue: number | null;
  id: number;
}) => {
  const [error, setError] = useState("");
  const [newDerivationType, setNewDerivationType] =
    useState<PriceListDerivationType>(derivationType);
  const [newDerivationValue, setNewDerivationValue] = useState(derivationValue);
  const [previousDerivation, setPreviousDerivation] = useState({
    type: derivationType,
    value: derivationValue,
  });
  if (
    derivationType !== previousDerivation.type ||
    derivationValue !== previousDerivation.value
  ) {
    setPreviousDerivation({ type: derivationType, value: derivationValue });
    setNewDerivationType(derivationType);
    setNewDerivationValue(derivationValue);
  }

  const updateDerivedPriceList = usePriceListUpdateDerived(id);

  const onSubmit = (type: PriceListDerivationType, value: number | null) => {
    setError("");
    updateDerivedPriceList.mutate(
      {
        derivationType: type,
        derivationValue: value,
      },
      {
        onSuccess: () => {
          setActive(false);
        },
      },
    );
  };

  return (
    <Dialog active={active} onDismiss={() => setActive(false)}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={() => setActive(false)}
        title="Update top level discount"
        description="Any product item with their price rule set to 'Inherit' will have this discount applied"
      />
      <DialogContent className="flex flex-col gap-4">
        {error && <Alert type="error" message={error} />}
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <InputLabel>Discount type</InputLabel>
            <InputContainer>
              <InputSelect
                defaultValue={newDerivationType}
                onChange={(e) => {
                  const newType = e.target.value as "percentage" | "flat";
                  setNewDerivationType(newType);
                }}
              >
                <InputOption value="percentage">Percentage</InputOption>
                <InputOption value="flat">Flat</InputOption>
              </InputSelect>
            </InputContainer>
          </div>
          <div className="flex flex-col gap-1">
            <InputLabel>
              Discount amount{" "}
              {newDerivationType === "percentage" ? "(%)" : "(£)"}
            </InputLabel>
            <InputContainer>
              {newDerivationType === "percentage" && (
                <>
                  <span>%</span>
                  <InputNumber
                    defaultValue={newDerivationValue ?? undefined}
                    min={0}
                    max={100}
                    step={0.1}
                    onChange={(e) =>
                      setNewDerivationValue(parseFloat(e.target.value))
                    }
                  />
                </>
              )}
              {newDerivationType === "flat" && (
                <InputCurrency
                  showPrefix
                  allowNegative
                  defaultValue={newDerivationValue ?? undefined}
                  onChange={(e) =>
                    setNewDerivationValue(
                      formatPriceInputToApi(e.target.value) ?? 0,
                    )
                  }
                />
              )}
            </InputContainer>
          </div>
          <div className="flex flex-col gap-1">
            <InputLabel>Container type</InputLabel>
            <InputContainer>
              <InputSelect disabled={true} className="text-zinc-400">
                <InputOption>Any</InputOption>
              </InputSelect>
            </InputContainer>
          </div>
        </div>
      </DialogContent>
      <DialogCTA>
        <Button
          color="red"
          loading={updateDerivedPriceList.isPending}
          onClick={() => onSubmit("custom", null)}
        >
          Delete
        </Button>
        <Button
          loading={updateDerivedPriceList.isPending}
          onClick={() => onSubmit(newDerivationType, newDerivationValue)}
        >
          Update
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
